import React, { useEffect, useMemo, useState } from "react";
import axios from "../api/client";
import { fetchFuelTypes } from "../api/client";
import { useTranslation } from "react-i18next";

function isUnsupportedScheduledTarget(target) {
  return String(target?.site || "").toLowerCase() === "abc_corp";
}

export default function AdminScrapeTargets() {
  const { t } = useTranslation();
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    postalCode: "",
    site: "neste_corp",
    litres: "1000",
    fuelType: "",
    runHour: "8",
  });
  const [fuelTypes, setFuelTypes] = useState([]);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cleanupHour, setCleanupHour] = useState("23");
  const [cleanupMessage, setCleanupMessage] = useState("");
  const [cleanupSaving, setCleanupSaving] = useState(false);
  const [runLogs, setRunLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    loadTargets();
    loadSchedulerSettings();
    loadRunLogs();
  }, []);

  useEffect(() => {
    fetchFuelTypes()
      .then((groups) => {
        const variants = [];
        (groups || []).forEach((group) => {
          group.variants?.forEach((variant) => {
            variants.push({
              id: variant.id,
              label: `${group.label} – ${variant.label}`,
            });
          });
        });
        setFuelTypes(variants);
      })
      .catch(() => setFuelTypes([]));
  }, []);

  const sortedTargets = useMemo(() => {
    return [...targets].sort((a, b) => {
      const postalA = a?.postal_code || "";
      const postalB = b?.postal_code || "";
      if (postalA === postalB) {
        return (a?.site || "").localeCompare(b?.site || "");
      }
      return postalA.localeCompare(postalB);
    });
  }, [targets]);

  const siteOptions = [
    { value: "neste_corp", label: t("admin.targets.siteLabels.neste") },
    { value: "st1_for_corporate", label: t("admin.targets.siteLabels.st1") },
  ];

  async function loadTargets() {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/api/targets");
      setTargets(data || []);
    } catch (err) {
      console.error("Failed to load targets", err);
      setError(t("admin.targets.errors.load"));
    } finally {
      setLoading(false);
    }
  }

  async function loadSchedulerSettings() {
    try {
      const { data } = await axios.get("/api/scheduler/settings");
      if (data?.cleanupHour !== undefined && data?.cleanupHour !== null) {
        setCleanupHour(String(data.cleanupHour));
      }
    } catch (err) {
      console.error("Failed to load scheduler settings", err);
      setError(t("admin.targets.errors.settings"));
    }
  }

  async function loadRunLogs() {
    setLogsLoading(true);
    try {
      const { data } = await axios.get("/api/scheduler/runs");
      setRunLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load scheduler runs", err);
    } finally {
      setLogsLoading(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        postal_code: formState.postalCode.trim(),
        site: formState.site,
        litres: formState.litres ? Number(formState.litres) : 1000,
        fuelType: formState.fuelType || undefined,
        runHour: formState.runHour ? Number(formState.runHour) : 8,
      };
      if (!payload.postal_code || !payload.site) {
        setError(t("admin.targets.errors.required"));
        setSubmitting(false);
        return;
      }
      await axios.post("/api/targets", payload);
      setMessage(t("admin.targets.added"));
      setFormState({ postalCode: "", site: "neste_corp", litres: "1000", fuelType: "", runHour: "8" });
      await loadTargets();
    } catch (err) {
      console.error("Failed to add target", err);
      setError(
        err.response?.data?.message || t("admin.targets.errors.add")
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggle(target) {
    try {
      await axios.patch(`/api/targets/${target.id}`, {
        active: !target.active,
      });
      await loadTargets();
    } catch (err) {
      console.error("Failed to toggle target", err);
      setError(t("admin.targets.errors.toggle"));
    }
  }

  async function handleDelete(target) {
    if (!window.confirm(t("admin.targets.confirmDelete"))) return;
    try {
      await axios.delete(`/api/targets/${target.id}`);
      await loadTargets();
    } catch (err) {
      console.error("Failed to delete target", err);
      setError(t("admin.targets.errors.delete"));
    }
  }

  async function handleRunHourChange(target, nextHour) {
    try {
      await axios.patch(`/api/targets/${target.id}`, {
        runHour: Number(nextHour),
      });
      await loadTargets();
    } catch (err) {
      console.error("Failed to update run hour", err);
      setError(t("admin.targets.errors.hour"));
    }
  }

  const hourOptions = useMemo(
    () => Array.from({ length: 24 }, (_, h) => ({ value: h, label: `${h.toString().padStart(2, "0")}:00` })),
    []
  );

  async function handleCleanupSave(e) {
    e.preventDefault();
    setCleanupSaving(true);
    setCleanupMessage("");
    setError("");
    try {
      await axios.put("/api/scheduler/settings", { cleanupHour: Number(cleanupHour) });
      setCleanupMessage(t("admin.targets.cleanupSaved"));
    } catch (err) {
      console.error("Failed to update cleanup hour", err);
      setError(t("admin.targets.errors.cleanup"));
    } finally {
      setCleanupSaving(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="bg-white border rounded-xl shadow p-4">
        <h2 className="text-xl font-semibold mb-3">{t("admin.targets.cleanupTitle")}</h2>
        <p className="text-sm text-gray-600 mb-3">
          {t("admin.targets.cleanupDescription")}
        </p>
        <form onSubmit={handleCleanupSave} className="flex flex-col md:flex-row gap-4 items-center">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">{t("admin.targets.cleanupHour")}</label>
            <select
              value={cleanupHour}
              onChange={(e) => setCleanupHour(e.target.value)}
              className="border rounded-lg px-3 py-2 bg-white"
            >
              {hourOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={cleanupSaving}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {cleanupSaving ? t("admin.rules.saving") : t("admin.users.save")}
            </button>
            {cleanupMessage && <span className="text-green-600 text-sm">{cleanupMessage}</span>}
          </div>
        </form>
      </div>
      <div className="bg-white border rounded-xl shadow p-4">
        <h1 className="text-2xl font-semibold mb-4">
          {t("admin.targets.title")}
        </h1>
        <p className="text-sm text-gray-600 mb-4">
          {t("admin.targets.description")}
        </p>
        <form
          onSubmit={handleAdd}
        className="grid grid-cols-1 md:grid-cols-5 gap-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("admin.targets.postal")}
            </label>
            <input
              type="text"
              value={formState.postalCode}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  postalCode: e.target.value,
                }))
              }
              className="w-full border rounded-lg px-3 py-2"
              placeholder={t("admin.targets.postalPlaceholder")}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("admin.targets.site")}
            </label>
            <select
              value={formState.site}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, site: e.target.value }))
              }
              className="w-full border rounded-lg px-3 py-2 bg-white"
            >
              {siteOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("admin.targets.litres")}
            </label>
            <input
              type="number"
              min="1"
              value={formState.litres}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, litres: e.target.value }))
              }
              className="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("admin.targets.fuelType")}
            </label>
            <select
              value={formState.fuelType}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  fuelType: e.target.value,
                }))
              }
              className="w-full border rounded-lg px-3 py-2 bg-white"
            >
              <option value="">{t("admin.targets.default")}</option>
              {fuelTypes.map((variant) => (
                <option key={variant.id} value={variant.id}>
                  {variant.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              {t("admin.targets.time")}
            </label>
            <select
              value={formState.runHour}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  runHour: e.target.value,
                }))
              }
              className="w-full border rounded-lg px-3 py-2 bg-white"
            >
              {hourOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-5 flex items-center gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? t("admin.rules.saving") : t("admin.targets.add")}
            </button>
            {message && <span className="text-green-600 text-sm">{message}</span>}
            {error && <span className="text-red-600 text-sm">{error}</span>}
          </div>
        </form>
      </div>

      <div className="bg-white border rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">{t("admin.targets.current")}</h2>
          <button
            onClick={loadTargets}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-60"
          >
            {loading ? t("admin.database.refreshing") : t("admin.targets.refreshList")}
          </button>
        </div>
        {sortedTargets.length === 0 ? (
          <p className="text-gray-500 text-sm">{t("admin.targets.noTargets")}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-gray-600">
                  <th className="py-2 px-3 text-left">{t("admin.targets.postal")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.site")}</th>
                  <th className="py-2 px-3 text-right">{t("admin.targets.litres")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.abcBatch.fuel")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.time")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.status")}</th>
                  <th className="py-2 px-3 text-right">{t("admin.targets.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedTargets.map((target) => {
                  const unsupported = isUnsupportedScheduledTarget(target);
                  return (
                  <tr key={target.id} className="border-b last:border-b-0">
                    <td className="py-2 px-3">{target.postal_code}</td>
                    <td className="py-2 px-3 uppercase">
                      {target.site}
                      {unsupported && (
                        <span className="block normal-case text-xs text-amber-700">
                          {t("admin.targets.unsupported")}
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">{target.litres}</td>
                    <td className="py-2 px-3">{target.fuel_type || "—"}</td>
                    <td className="py-2 px-3">
                      <select
                        value={(typeof target.run_hour === "number" ? target.run_hour : 8).toString()}
                        onChange={(e) => handleRunHourChange(target, e.target.value)}
                        disabled={unsupported}
                        className="border rounded px-2 py-1 bg-white text-sm"
                      >
                        {hourOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 px-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          unsupported
                            ? "bg-amber-100 text-amber-800"
                            : target.active ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {unsupported ? t("admin.targets.skipped") : target.active ? t("admin.targets.active") : t("admin.targets.disabled")}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right space-x-2">
                      {!unsupported && (
                        <button
                          onClick={() => handleToggle(target)}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {target.active ? t("admin.targets.disable") : t("admin.targets.activate")}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(target)}
                        className="text-sm text-red-600 hover:underline"
                      >
                        Poista
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="bg-white border rounded-xl shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold">{t("admin.targets.history")}</h2>
          <button
            onClick={loadRunLogs}
            disabled={logsLoading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-60"
          >
            {logsLoading ? t("admin.database.refreshing") : t("admin.database.refresh")}
          </button>
        </div>
        {runLogs.length === 0 ? (
          <p className="text-gray-500 text-sm">
            {logsLoading ? t("common.loading") : t("admin.targets.noLogs")}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-gray-600">
                  <th className="py-2 px-3 text-left">{t("admin.targets.started")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.hour")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.status")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.succeeded")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.reason")}</th>
                  <th className="py-2 px-3 text-left">{t("admin.targets.videos")}</th>
                </tr>
              </thead>
              <tbody>
                {runLogs.map((log) => (
                  <tr key={log.id} className="border-b last:border-b-0">
                    <td className="py-2 px-3">
                      {log.started_at ? new Date(log.started_at).toLocaleString("fi-FI") : "–"}
                    </td>
                    <td className="py-2 px-3">{`${String(log.run_hour).padStart(2, "0")}:00`}</td>
                    <td className="py-2 px-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          log.status === "ok"
                            ? "bg-green-100 text-green-700"
                            : log.status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {log.status === "ok"
                          ? t("admin.targets.success")
                          : log.status === "failed"
                          ? t("admin.targets.failed")
                          : t("admin.targets.skipped")}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      {log.success_count}/{log.total_targets}
                    </td>
                    <td className="py-2 px-3 text-gray-700">
                      {log.reason || "—"}
                    </td>
                    <td className="py-2 px-3">
                      {(log.artifact_videos || []).length === 0 ? (
                        <span className="text-gray-400 text-xs">–</span>
                      ) : (
                        <ul className="space-y-1">
                          {log.artifact_videos.map((video, idx) => (
                            <li key={`${log.id}-v-${idx}`} className="text-xs text-gray-700 break-all">
                              {video.video_path}
                              {video.postal_code ? ` (${video.postal_code})` : ""}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
